import { combineReducers, configureStore } from "@reduxjs/toolkit"
import { useDispatch, useSelector, useStore } from "react-redux"
import type { TypedUseSelectorHook } from "react-redux"
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from "redux-persist"

import {
  collectionNavigationReducer,
  clearContactReturnHighlight,
  clearCollectionNavigationContext,
  contactReturnHighlightScopesMatch,
  selectContactReturnHighlight,
  selectCollectionNavigationContext,
  selectCollectionNavigationContextForKind,
  setCollectionNavigationContext,
  setContactReturnHighlight,
  type ContactReturnHighlight,
  type ContactReturnHighlightScope,
} from "@/lib/store/collection-navigation-slice"
import persistStorage from "@/lib/store/persist-storage"
import {
  acknowledgeContactUploadSession,
  clearContactUploadSession,
  contactUploadSessionMatchesIdentity,
  contactUploadSessionReducer,
  hydrateContactUploadSession,
  selectContactUploadInProgress,
  selectContactUploadSession,
  setContactUploadDialogOpen,
  startContactUploadUploadingSession,
  startContactUploadParsedSession,
  startContactUploadProcessing,
  updateContactUploadImportOptions,
  updateContactUploadMappingRows,
  updateContactUploadProgress,
  type ContactUploadColumnMappingRow,
  type ContactUploadHydratedPayload,
  type ContactUploadSessionState,
  type ContactUploadSessionStatus,
  type ContactUploadStep,
} from "@/lib/store/contact-upload-session-slice"
import {
  resetSessionBootstrap,
  sessionBootstrapReceived,
  sessionBootstrapReducer,
  selectHasPhoneSystem,
  type SessionBootstrapPayload,
} from "@/lib/store/session-bootstrap-slice"

export {
  resetSessionBootstrap,
  sessionBootstrapReceived,
  selectHasPhoneSystem,
  type SessionBootstrapPayload,
  setCollectionNavigationContext,
  clearCollectionNavigationContext,
  setContactReturnHighlight,
  clearContactReturnHighlight,
  selectCollectionNavigationContext,
  selectCollectionNavigationContextForKind,
  selectContactReturnHighlight,
  contactReturnHighlightScopesMatch,
  type ContactReturnHighlight,
  type ContactReturnHighlightScope,
  acknowledgeContactUploadSession,
  clearContactUploadSession,
  contactUploadSessionMatchesIdentity,
  hydrateContactUploadSession,
  selectContactUploadInProgress,
  selectContactUploadSession,
  setContactUploadDialogOpen,
  startContactUploadUploadingSession,
  startContactUploadParsedSession,
  startContactUploadProcessing,
  updateContactUploadImportOptions,
  updateContactUploadMappingRows,
  updateContactUploadProgress,
  type ContactUploadColumnMappingRow,
  type ContactUploadHydratedPayload,
  type ContactUploadSessionState,
  type ContactUploadSessionStatus,
  type ContactUploadStep,
}

const collectionNavigationPersistConfig = {
  key: "collectionNavigation",
  storage: persistStorage,
  whitelist: ["context"],
}

const contactUploadSessionPersistConfig = {
  key: "contactUploadSession",
  storage: persistStorage,
  whitelist: [
    "status",
    "workspaceId",
    "userId",
    "uploadId",
    "step",
    "phase",
    "processed",
    "total",
    "summary",
    "errorMessage",
    "importOptions",
    "columnMappingRows",
  ],
}

const rootReducer = combineReducers({
  sessionBootstrap: sessionBootstrapReducer,
  collectionNavigation: persistReducer(
    collectionNavigationPersistConfig,
    collectionNavigationReducer,
  ),
  contactUploadSession: persistReducer(
    contactUploadSessionPersistConfig,
    contactUploadSessionReducer,
  ),
})

export function makeStore() {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }),
  })
}

export function makePersistStore(store: ReturnType<typeof makeStore>) {
  return persistStore(store)
}

export type AppStore = ReturnType<typeof makeStore>
export type RootState = ReturnType<typeof rootReducer>
export type AppDispatch = AppStore["dispatch"]

export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
export const useAppStore: () => AppStore = useStore
